import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Collapse.module.css';

const TRANSITION_MS = 150;

type CollapseProps = {
  summary: ReactNode;
  summaryOpen?: ReactNode;
  children: ReactNode;
  chevron?: ReactNode;
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
};

export const Collapse = ({
  summary,
  summaryOpen,
  children,
  chevron,
  defaultOpen = false,
  onOpenChange,
}: CollapseProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isExpanded, setIsExpanded] = useState(defaultOpen);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const nextIsOpen = !isOpen;
      onOpenChange?.(nextIsOpen);
      if (isOpen) {
        setIsExpanded(false);
        closeTimeoutRef.current = setTimeout(() => {
          setIsOpen(false);
          closeTimeoutRef.current = null;
        }, TRANSITION_MS);
      } else {
        setIsOpen(true);
        requestAnimationFrame(() => {
          setIsExpanded(true);
        });
      }
    },
    [isOpen, onOpenChange]
  );

  return (
    <details className={styles.root} open={isOpen} data-expanded={isExpanded ? '' : undefined}>
      <summary className={styles.summary} onClick={handleToggle}>
        <span className={styles.summaryContent}>
          {summaryOpen != null ? (
            <>
              <span className={styles.summaryClosed}>{summary}</span>
              <span className={styles.summaryOpen}>{summaryOpen}</span>
            </>
          ) : (
            summary
          )}
        </span>
        {chevron != null ? <span className={styles.chevron}>{chevron}</span> : null}
      </summary>
      <div className={styles.content}>
        <div className={styles.contentInner}>{children}</div>
      </div>
    </details>
  );
};
